import { Component, inject, signal, viewChild } from '@angular/core';

import { firstValueFrom } from 'rxjs';

import type { AskTemplate } from '@core/ask-hr-templates-types';
import { TicketService } from '@core/ask-hr-tickets-logic';
import type { CreateAskTicket } from '@core/ask-hr-tickets-types';
import { TranslatePipe } from '@core/i18n-legacy';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
} from '@core/user-interface-legacy';

import type { AskHrDynamicFormConfig } from '../ask-templates-form.service';
import { CreateTicketComponent } from '../create-ticket/create-ticket.component';

@Component({
  selector: 'ask-hr-create-ticket-dialog',
  template: `
    <rt-dialog-header
      [heading]="'tlc.ask_hr.requests.create.heading' | translate"
      [subheading]="'tlc.ask_hr.requests.create.subheading' | translate"
    />
    <rt-dialog-body layout-justify-center>
      <div class="dialog-body">
        <tlc-create-ticket
          #createTicketComponent
          [template]="dialogData.template"
          (ticketChanges)="ticketData.set($event)"
        />
      </div>
    </rt-dialog-body>
    <rt-dialog-footer>
      <rt-button (click)="dialogRef.close()">
        {{ 'tlc.ask_hr.requests.create.cancel' | translate }}
      </rt-button>
      <rt-button
        variant="primary"
        [disabled]="!createTicketComponent.isFormValid() || isLoading()"
        [loading]="isLoading()"
        (click)="onSubmit()"
      >
        {{ 'tlc.ask_hr.requests.create.submit' | translate }}
      </rt-button>
    </rt-dialog-footer>
  `,
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    TranslatePipe,
    DialogFooterComponent,
    ButtonComponent,
    CreateTicketComponent,
    ButtonLoadingDirective,
  ],
  styleUrls: ['./create-ticket-dialog.component.less'],
})
export class CreateTicketDialogComponent extends DialogComponent<{ template: AskTemplate }, void> {
  private readonly ticketService = inject(TicketService);
  protected ticketData = signal<CreateAskTicket<AskHrDynamicFormConfig> | null>(null);
  protected createTicketComponent = viewChild.required(CreateTicketComponent);

  protected isLoading = signal(false);

  public async onSubmit() {
    const ticketData = this.ticketData();
    if (!ticketData || this.isLoading()) return;

    this.isLoading.set(true);
    const result = await firstValueFrom(this.ticketService.createTicket(ticketData));
    if (result.isSuccess) {
      this.createTicketComponent().resetFormStatus();
      this.dialogRef.close();
    }
    this.isLoading.set(false);
  }
}
